import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalPersistStorage } from '@/stores/persistStorage';
import { uuid } from '@/lib/uuid';
import { useAuthStore } from '@/stores/authStore';
import {
  writeHabit,
  deleteHabit as fsDeleteHabit,
  writeCompletion,
  deleteCompletion as fsDeleteCompletion,
} from '@/stores/sync';
import type { Habit, HabitCompletion } from '@/types';
import { toDateKey } from '@/lib/dateKey';
import { isHabitDueOnDay, type WeekStart } from '@/lib/week';
import { useUIStore } from '@/stores/uiStore';

function syncHabit(habit: Habit) {
  const { uid, authMode } = useAuthStore.getState();
  if (authMode === 'authenticated' && uid) {
    void writeHabit(uid, habit);
  }
}

function syncDeleteHabit(habitId: string) {
  const { uid, authMode } = useAuthStore.getState();
  if (authMode === 'authenticated' && uid) {
    void fsDeleteHabit(uid, habitId);
  }
}

function syncCompletion(completion: HabitCompletion) {
  const { uid, authMode } = useAuthStore.getState();
  if (authMode === 'authenticated' && uid) {
    void writeCompletion(uid, completion);
  }
}

function syncDeleteCompletion(completionId: string) {
  const { uid, authMode } = useAuthStore.getState();
  if (authMode === 'authenticated' && uid) {
    void fsDeleteCompletion(uid, completionId);
  }
}

/**
 * Streak = consecutive DUE days completed, walking back from today. Days the
 * habit isn't scheduled on (weekends for a weekdays habit, the off-days of a
 * custom habit) are SKIPPED — they neither extend nor break the streak. The old
 * version counted raw calendar days, so every non-daily habit reset its streak
 * on the first non-due day (a perfect weekdays habit was stuck at ≤5 and reset
 * to 1 each Monday). Frequency comes from the habit; week start from uiStore.
 */
const computeStreak = (
  completions: HabitCompletion[],
  habit: Habit,
  weekStart: WeekStart,
): number => {
  const dates = new Set(completions.filter((c) => c.habitId === habit.id).map((c) => c.date));
  const cursor = new Date();
  // Today being due-but-not-yet-done is not a break — the user still has the
  // rest of today. Start the walk at yesterday in that case.
  if (isHabitDueOnDay(habit, cursor.getDay(), weekStart) && !dates.has(toDateKey(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
  }
  let streak = 0;
  // The 3660-day (~10yr) cap bounds the degenerate "never due" case: a custom
  // habit with an empty customDays list is due on no day, so nothing else would
  // ever stop the walk.
  for (let i = 0; i < 3660; i += 1) {
    if (isHabitDueOnDay(habit, cursor.getDay(), weekStart)) {
      if (dates.has(toDateKey(cursor))) streak += 1;
      else break;
    }
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
};

export interface HabitState {
  habits: Habit[];
  completions: HabitCompletion[];
  addHabit: (
    input: Omit<Habit, 'id' | 'createdAt' | 'updatedAt' | 'currentStreak' | 'longestStreak'>,
  ) => Habit;
  updateHabit: (id: string, patch: Partial<Omit<Habit, 'id' | 'createdAt'>>) => void;
  removeHabit: (id: string) => void;
  logCompletion: (habitId: string, note?: string) => HabitCompletion | null;
  logCompletionForDate: (habitId: string, dateKey: string) => HabitCompletion | null;
  removeCompletion: (habitId: string, date: string) => void;
}

export const useHabitStore = create<HabitState>()(
  persist(
    (set, get) => ({
      habits: [],
      completions: [],
      addHabit: (input) => {
        const now = new Date();
        const habit: Habit = {
          ...input,
          id: uuid(),
          currentStreak: 0,
          longestStreak: 0,
          createdAt: now,
          updatedAt: now,
        };
        set((s) => ({ habits: [...s.habits, habit] }));
        syncHabit(habit);
        return habit;
      },
      updateHabit: (id, patch) => {
        set((s) => ({
          habits: s.habits.map((h) =>
            h.id === id ? { ...h, ...patch, updatedAt: new Date() } : h,
          ),
        }));
        const updated = get().habits.find((h) => h.id === id);
        if (updated) syncHabit(updated);
      },
      removeHabit: (id) => {
        // Collect completion IDs before removing them
        const completionIds = get()
          .completions.filter((c) => c.habitId === id)
          .map((c) => c.id);
        set((s) => ({
          habits: s.habits.filter((h) => h.id !== id),
          completions: s.completions.filter((c) => c.habitId !== id),
        }));
        syncDeleteHabit(id);
        for (const cid of completionIds) {
          syncDeleteCompletion(cid);
        }
      },
      logCompletion: (habitId, note) => {
        const now = new Date();
        const date = toDateKey(now);
        const existing = get().completions.find((c) => c.habitId === habitId && c.date === date);
        if (existing) return null;
        const completion: HabitCompletion = {
          id: uuid(),
          habitId,
          date,
          completedAt: now,
          ...(note ? { note } : {}),
        };
        set((s) => {
          const completions = [...s.completions, completion];
          const weekStart = useUIStore.getState().weekStart;
          return {
            completions,
            habits: s.habits.map((h) => {
              if (h.id !== habitId) return h;
              const newStreak = computeStreak(completions, h, weekStart);
              return {
                ...h,
                currentStreak: newStreak,
                longestStreak: Math.max(h.longestStreak, newStreak),
                updatedAt: now,
              };
            }),
          };
        });
        syncCompletion(completion);
        const updatedHabit = get().habits.find((h) => h.id === habitId);
        if (updatedHabit) syncHabit(updatedHabit);
        return completion;
      },
      logCompletionForDate: (habitId, dateKey) => {
        const now = new Date();
        const existing = get().completions.find((c) => c.habitId === habitId && c.date === dateKey);
        if (existing) return null;
        const completion: HabitCompletion = {
          id: uuid(),
          habitId,
          date: dateKey,
          completedAt: now,
        };
        set((s) => {
          const completions = [...s.completions, completion];
          const weekStart = useUIStore.getState().weekStart;
          return {
            completions,
            habits: s.habits.map((h) => {
              if (h.id !== habitId) return h;
              const newStreak = computeStreak(completions, h, weekStart);
              return {
                ...h,
                currentStreak: newStreak,
                longestStreak: Math.max(h.longestStreak, newStreak),
                updatedAt: now,
              };
            }),
          };
        });
        syncCompletion(completion);
        const updatedHabit = get().habits.find((h) => h.id === habitId);
        if (updatedHabit) syncHabit(updatedHabit);
        return completion;
      },
      removeCompletion: (habitId, date) => {
        // Find the completion ID before removing
        const removed = get().completions.find((c) => c.habitId === habitId && c.date === date);
        set((s) => {
          const completions = s.completions.filter(
            (c) => !(c.habitId === habitId && c.date === date),
          );
          const weekStart = useUIStore.getState().weekStart;
          return {
            completions,
            habits: s.habits.map((h) => {
              if (h.id !== habitId) return h;
              return {
                ...h,
                currentStreak: computeStreak(completions, h, weekStart),
                updatedAt: new Date(),
              };
            }),
          };
        });
        if (removed) syncDeleteCompletion(removed.id);
        const updatedHabit = get().habits.find((h) => h.id === habitId);
        if (updatedHabit) syncHabit(updatedHabit);
      },
    }),
    { name: 'brainday:habits', storage: conditionalPersistStorage<HabitState>() },
  ),
);
